"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";

interface BundleInfo {
  name: string;
  size: string;
  description: string;
  color: string;
}

export default function BundleAnalysis() {
  const [buildTime, setBuildTime] = useState<string>("");

  useEffect(() => {
    setBuildTime(new Date().toLocaleString());
  }, []);

  const regularBundleInfo: BundleInfo[] = [
    {
      name: "Main Bundle (with all imports)",
      size: "~400KB",
      description: "Includes Chart.js, Lodash, date-fns, and all components",
      color: "bg-red-100 border-red-300 text-red-800",
    },
    {
      name: "Chart.js",
      size: "~200KB",
      description: "Complete charting library loaded upfront",
      color: "bg-orange-100 border-orange-300 text-orange-800",
    },
    {
      name: "Lodash",
      size: "~70KB",
      description: "Utility library loaded immediately",
      color: "bg-yellow-100 border-yellow-300 text-yellow-800",
    },
    {
      name: "date-fns",
      size: "~20KB",
      description: "Date formatting library in main bundle",
      color: "bg-blue-100 border-blue-300 text-blue-800",
    },
  ];

  const dynamicBundleInfo: BundleInfo[] = [
    {
      name: "Main Bundle (minimal)",
      size: "~100KB",
      description: "Only core app code and React",
      color: "bg-green-100 border-green-300 text-green-800",
    },
    {
      name: "Chart Component Chunk",
      size: "~200KB",
      description: "Loaded only when chart is requested",
      color: "bg-emerald-100 border-emerald-300 text-emerald-800",
    },
    {
      name: "Table Component Chunk",
      size: "~90KB",
      description: "Loaded only when table is requested",
      color: "bg-teal-100 border-teal-300 text-teal-800",
    },
    {
      name: "Calculator Component Chunk",
      size: "~70KB",
      description: "Loaded only when calculator is requested",
      color: "bg-cyan-100 border-cyan-300 text-cyan-800",
    },
  ];

  const performanceMetrics = [
    {
      metric: "First Contentful Paint (FCP)",
      regular: "2.1s",
      dynamic: "1.4s",
      improvement: "33% faster",
    },
    {
      metric: "Largest Contentful Paint (LCP)",
      regular: "2.8s",
      dynamic: "1.9s",
      improvement: "32% faster",
    },
    {
      metric: "Time to Interactive (TTI)",
      regular: "3.5s",
      dynamic: "2.2s",
      improvement: "37% faster",
    },
    {
      metric: "Total Blocking Time (TBT)",
      regular: "400ms",
      dynamic: "200ms",
      improvement: "50% reduction",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Bundle Analysis & Performance Impact
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
            Detailed comparison of bundle sizes and performance metrics between
            regular imports and dynamic imports.
          </p>
          <Link
            href="/"
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            ← Back to Demo
          </Link>
        </div>

        {/* Bundle Size Comparison */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Regular Imports */}
          <div className="bg-white rounded-lg shadow-lg border p-6">
            <h2 className="text-2xl font-bold text-red-600 mb-4">
              ❌ Regular Imports
            </h2>
            <div className="space-y-4">
              {regularBundleInfo.map((bundle, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-2 ${bundle.color}`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-semibold">{bundle.name}</h3>
                    <span className="font-bold">{bundle.size}</span>
                  </div>
                  <p className="text-sm">{bundle.description}</p>
                </div>
              ))}

              <div className="bg-red-50 border-2 border-red-300 p-4 rounded-lg mt-4">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-red-800">
                    Total Initial Bundle:
                  </span>
                  <span className="text-2xl font-bold text-red-600">
                    ~400KB
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Dynamic Imports */}
          <div className="bg-white rounded-lg shadow-lg border p-6">
            <h2 className="text-2xl font-bold text-green-600 mb-4">
              ✅ Dynamic Imports
            </h2>
            <div className="space-y-4">
              {dynamicBundleInfo.map((bundle, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-2 ${bundle.color}`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-semibold">{bundle.name}</h3>
                    <span className="font-bold">{bundle.size}</span>
                  </div>
                  <p className="text-sm">{bundle.description}</p>
                </div>
              ))}

              <div className="bg-green-50 border-2 border-green-300 p-4 rounded-lg mt-4">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-green-800">
                    Initial Bundle:
                  </span>
                  <span className="text-2xl font-bold text-green-600">
                    ~100KB
                  </span>
                </div>
                <p className="text-sm text-green-700 mt-1">
                  75% smaller initial bundle!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="bg-white rounded-lg shadow-lg border p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            ⚡ Performance Impact
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold">
                    Core Web Vital
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-red-600">
                    Regular Imports
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-green-600">
                    Dynamic Imports
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-blue-600">
                    Improvement
                  </th>
                </tr>
              </thead>
              <tbody>
                {performanceMetrics.map((metric, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-3 px-4 font-medium">{metric.metric}</td>
                    <td className="py-3 px-4 text-red-600 font-mono">
                      {metric.regular}
                    </td>
                    <td className="py-3 px-4 text-green-600 font-mono">
                      {metric.dynamic}
                    </td>
                    <td className="py-3 px-4 text-blue-600 font-semibold">
                      {metric.improvement}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Real-World Benefits */}
        <div className="bg-white rounded-lg shadow-lg border p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            🌍 Real-World Benefits
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-3">
                User Experience
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Faster initial page loads improve user perception
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Reduced bounce rates on slow connections
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Better experience on mobile devices
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Components load only when needed
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-3">
                Business Impact
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Better SEO rankings (Core Web Vitals)
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Reduced bandwidth costs
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Higher conversion rates
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Better accessibility compliance
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Implementation Guide */}
        <div className="bg-white rounded-lg shadow-lg border p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            🛠️ Implementation Best Practices
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-blue-600 mb-3">
                When to Use Dynamic Imports
              </h3>
              <div className="bg-blue-50 p-4 rounded-lg">
                <ul className="space-y-2 text-blue-800">
                  <li>
                    • Large third-party libraries (Chart.js, Monaco Editor,
                    etc.)
                  </li>
                  <li>• Components used conditionally or in modals</li>
                  <li>• Route-specific functionality</li>
                  <li>• Feature flags or A/B test components</li>
                  <li>• Admin or power-user features</li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-orange-600 mb-3">
                Code Example
              </h3>
              <div className="bg-black p-4 rounded-lg overflow-x-auto">
                <pre className="text-sm">
                  <code>{`// ❌ Regular import - included in main bundle
import HeavyComponent from './HeavyComponent';

// ✅ Dynamic import - code-split into separate chunk
const DynamicHeavyComponent = dynamic(
  () => import('./HeavyComponent'),
  {
    loading: () => <LoadingSpinner />,
    ssr: false // Disable SSR if not needed
  }
);`}</code>
                </pre>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-green-600 mb-3">
                Tips for Success
              </h3>
              <div className="bg-green-50 p-4 rounded-lg">
                <ul className="space-y-2 text-green-800">
                  <li>
                    • Use meaningful loading states to improve perceived
                    performance
                  </li>
                  <li>
                    • Consider preloading critical dynamic components on
                    hover/focus
                  </li>
                  <li>• Analyze your bundle with @next/bundle-analyzer</li>
                  <li>
                    • Group related functionality into single dynamic imports
                  </li>
                  <li>• Monitor Core Web Vitals in production</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="text-center text-gray-500 border-t border-gray-200 pt-8">
          <p className="mb-2">Analysis generated at: {buildTime}</p>
          <p>
            Learn more about{" "}
            <a
              href="https://web.dev/code-splitting-suspense/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Code Splitting Best Practices
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
}
